import React from "react";
import {
    Play,
    RefreshCw,
    AlertTriangle,
    Info,
    CheckCircle,
} from "lucide-react";
import useGetEventDetailsApi from "../../hooks/useGetEventDetails";
import useGetResources from "../../hooks/useGetResources";
import KeepAliveInstance from "./KeepAliveInstance";
import ActionLogList from "../actions/ActionLogList";
import { Card, CardHeader, CardTitle, CardContent } from "components/ui/card";
import { Button } from "components/ui/button";
import { Badge } from "components/ui/badge";
import { Spinner } from "components/ui/spinner";

/**
 * The newest recorded keep-alive outcome for an instance, so the badge reflects
 * what any operator did rather than only this browser's history.
 */
function instanceStatus(entries, instanceId) {
    return (entries || []).find((entry) =>
        (entry.details?.instanceIds || []).includes(instanceId)
    );
}

/**
 * KeepAlive Component
 *
 * Displays the event's origin instances and allows users to keep them alive
 * using AWS Step Functions. Rendered as a card on the Actions tab.
 */
const KeepAlive = ({ eventId, environment, entries, logsError, onActionRecorded }) => {
    // API hooks
    const { eventDetails, error: eventError, isLoaded: eventLoaded } = useGetEventDetailsApi(eventId, environment);
    const {
        resources: instances,
        error: instancesError,
        isLoaded: instancesLoaded,
        refetchResources
    } = useGetResources(eventId, environment, "get_event_instances");

    // Event handlers
    const handleKeepAliveComplete = () => {
        if (onActionRecorded) {
            onActionRecorded();
        }
    };


    // Loading state
    if (!instancesLoaded || !eventLoaded) {
        return (
            <div className="flex justify-center p-4">
                <Spinner />
                <span className="sr-only">Loading instances...</span>
            </div>
        );
    }

    // Error state
    if (instancesError && eventError) {
        return (
            <div className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                Error loading data: {instancesError || eventError}
            </div>
        );
    }

    return (
        <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0 border-b border-border">
                <CardTitle className="flex items-center gap-2 text-base">
                    <Play className="h-4 w-4 text-primary" />
                    Keep Instances Alive
                </CardTitle>
                <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={refetchResources}
                    disabled={!instancesLoaded}
                >
                    <RefreshCw />
                    Refresh Instances
                </Button>
            </CardHeader>
            <CardContent className="space-y-3 pt-4">
                {/* Event details error */}
                {eventError && (
                    <div className="flex items-start gap-2 rounded-md border border-primary/30 bg-primary/10 px-4 py-3 text-sm">
                        <Info className="mt-0.5 h-4 w-4 shrink-0" />
                        <div>
                            <strong>Note:</strong> Unable to load event details.
                            <br />
                            <small>Error: {eventError}</small>
                        </div>
                    </div>
                )}

                {/* Instances error */}
                {instancesError && (
                    <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 px-4 py-3 text-sm text-warning">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                        <div>
                            <strong>Warning:</strong> Unable to load instances for this event.
                            <br />
                            <small>Error: {instancesError}</small>
                        </div>
                    </div>
                )}

                {/* Instances list */}
                {instances && instances.length > 0 ? (
                    <div className="space-y-3">
                        {instances.map((instance, index) => {
                            const keepAliveInfo = instanceStatus(entries, instance.instanceId);
                            const isKeepAliveCompleted = keepAliveInfo?.status === 'success';
                            const isKeepAliveFailed = keepAliveInfo?.status === 'failed';

                            return (
                                <div key={index} className="rounded-lg border border-border p-3">
                                    <div className="flex items-center justify-between gap-3">
                                        <div>
                                            <div className="mb-2 flex flex-wrap items-center gap-2">
                                                <strong>Instance ID:</strong>
                                                <code className="rounded bg-muted px-1.5 py-0.5 text-sm">{instance.instanceId}</code>
                                                <Badge variant={instance.state === 'running' ? 'success' : 'warning'}>
                                                    {instance.state}
                                                </Badge>
                                                <small className="text-muted-foreground">
                                                    {instance.instanceType}
                                                </small>
                                            </div>

                                            {keepAliveInfo && (
                                                <div className="flex flex-wrap items-center gap-2">
                                                    {isKeepAliveCompleted && (
                                                        <Badge variant="success">
                                                            <CheckCircle className="h-3.5 w-3.5" />
                                                            Keep Alive Done
                                                        </Badge>
                                                    )}
                                                    {isKeepAliveFailed && (
                                                        <Badge variant="destructive">
                                                            <AlertTriangle className="h-3.5 w-3.5" />
                                                            Keep Alive Failed
                                                        </Badge>
                                                    )}
                                                    <small className="text-muted-foreground">
                                                        {keepAliveInfo.timestamp} - {keepAliveInfo.user} - {keepAliveInfo.message}
                                                    </small>
                                                </div>
                                            )}
                                        </div>

                                        <div className="shrink-0">
                                            <KeepAliveInstance
                                                instanceId={instance.instanceId}
                                                environment={environment}
                                                eventId={eventId}
                                                onKeepAliveComplete={handleKeepAliveComplete}
                                                isCompleted={isKeepAliveCompleted}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="flex items-start gap-2 rounded-md border border-primary/30 bg-primary/10 px-4 py-3 text-sm">
                        <Info className="mt-0.5 h-4 w-4 shrink-0" />
                        <div>
                            <strong>No instances found for this event.</strong>
                            <br />
                            <small>
                                This could mean:
                                <ul className="ml-4 mt-2 list-disc">
                                    <li>The event is still starting up and instances haven't been created yet</li>
                                    <li>The instances don't have the event ID in their Name tag</li>
                                    <li>The event has finished and instances have been terminated</li>
                                </ul>
                            </small>
                        </div>
                    </div>
                )}
                <ActionLogList entries={entries} error={logsError} />
            </CardContent>
        </Card>
    );
};

export default KeepAlive;
